import _ from "lodash";
import {
  initiateAccommodationSearch,
  pollAccommodationSearch,
  storeSearchSessionMetadata,
} from "../../api";

import {
  ACCOMMODATION_SEARCH_PAYLOAD_SAVE,
  ACCOMMODATION_SEARCH_STOP,
  ACCOMMODATION_SEARCH_SAVE_META,
  ACCOMMODATION_SEARCH_SAVE_RESULTS,
  ACCOMMODATION_SEARCH_STATE_INIT,
  ACCOMMODATION_SEARCH_SET_CURRENT_POLLCOUNT,
  ACCOMMODATION_SEARCH_FILTERS_CHANGE,
  ACCOMMODATION_SEARCH_LOAD_STATE,
  ACCOMMODATION_SEARCH_RESET_FILTER,
  ACCOMMODATION_SEARCH_SESSION_LOADING,
  ACCOMMODATION_SEARCH_SESSION_IDLE,
  ACCOMMODATION_SYSTEM_INIT,
} from "./types";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { accSearchFormReset } from "../search/accommodation_search";

export const default_guest_nationality = import.meta.env.VITE_APP_FIXED_GUEST_NATIONALITY as string;

export const accSearchSystemInit = () => {
  return { type: ACCOMMODATION_SYSTEM_INIT };
};

const searchInit = () => {
  return { type: ACCOMMODATION_SEARCH_STATE_INIT };
};

function createSearchPayload({
  form,
  source_entity,
  target_currency,
  market_type,
  website_api_key,
}) {
  const { destination_meta } = form;
  let query_id = "";
  const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
  if (destination_meta?.location_type === "destination") {
    const dest_code = form?.destination_code?.[0];
    // Check if destination code is Base64
    if (base64regex.test(dest_code)) {
      query_id = dest_code;
    } else {
      const [lvl, id] = dest_code.split("___");
      query_id = `lv${lvl[3]}__${id}`;
    }
  } else {
    query_id = window.btoa(destination_meta.uid);
  }

  if (!base64regex.test(query_id)) {
    query_id = window.btoa(query_id);
  }

  const check_in = form.checkIn;
  const check_out = form.checkOut;

  const query = {
    id: [query_id],
    type: destination_meta?.location_type === "destination" ? "destination" : "hotel",
  };

  const rooms = form.rooms;

  const searchPayload = {
    source_entity,
    website_api_key,
    lang: "en",
    check_in,
    check_out,
    rooms,
    query,
    target_currency,
    market_type,
    nationality: "",
  };
  if (!!default_guest_nationality) {
    searchPayload.nationality = default_guest_nationality;
  } else {
    searchPayload.nationality = form.nationality;
  }

  return searchPayload;
}

function getSearchData(form) {
  const searchData = {
    checkIn: form.checkIn,
    checkOut: form.checkOut,
    adults: form.adults,
    children: form.children,
    children_ages: form.children_ages,
    rooms: form.rooms,
    destination_code: form.destination_code[0],
    destination_meta: form.destination_meta,
  };

  return searchData;
}

function saveAccSearchPayload({ searchPayload, searchData }) {
  return {
    type: ACCOMMODATION_SEARCH_PAYLOAD_SAVE,
    searchPayload,
    searchData,
  };
}

function saveAccSearchMeta({ searchMeta }) {
  return { type: ACCOMMODATION_SEARCH_SAVE_META, searchMeta };
}

function startSearchFail(
  msg = "We cannot initiate hotel search. We are sorry for the inconvenience.",
) {
  toast.error(msg, { duration: 5000 });
}

export const startSearch =
  ({ history, source_entity, market_type = "B2C" }) =>
  async (dispatch, getState) => {
    await dispatch(searchInit());
    const state = getState();
    const { currency: target_currency, api_key } = state.WebsiteMetadata.metadata;
    var form = {};

    form = {
      ...state.HotelSearchForm,
      adults: _.sum(state.HotelSearchForm.rooms.map((room) => room.adults)),
      children: _.sum(state.HotelSearchForm.rooms.map((room) => room.children)),
      children_ages: state.HotelSearchForm.rooms.map((room) => room.children_ages).flat(),
      rooms: state.HotelSearchForm.rooms,
    };

    const searchPayload = createSearchPayload({
      form,
      source_entity,
      market_type,
      target_currency,
      website_api_key: api_key,
    });

    const searchData = getSearchData(form);
    await dispatch(saveAccSearchPayload({ searchPayload, searchData }));

    const result = await initiateAccommodationSearch({
      payload: searchPayload,
    });
    if (!result) {
      startSearchFail();
      return null;
    }

    const searchMeta = { ...result, pollCount: 0, maxPollCount: 20 };
    const store_session_result = await storeSearchSessionMetadata({
      service_type: "ACC",
      session_data: {
        searchMeta,
        searchPayload,
        searchData,
      },
    });

    if (!store_session_result) {
      startSearchFail();
      return null;
    }

    await dispatch(
      saveAccSearchMeta({
        searchMeta: {
          ...searchMeta,
          page_session_id: store_session_result.session_id,
        },
      }),
    );

    dispatch(pollSearch({ history, market_type }));
    dispatch(accSearchFormReset());
    history.push(`/hotels/${store_session_result.session_id}`);
    return true;
  };

const stopSearch =
  ({ history }) =>
  (dispatch, getState) => {
    const hasResults = (getState()?.HotelSearch?.hotels ?? []).length > 0;
    dispatch({ type: ACCOMMODATION_SEARCH_STOP });

    if (!hasResults) {
      toast.error("We couldn't find any hotel results. We are sorry for incovenience", {
        duration: 3000,
      });
      history.push("/search_feedback/hotel");
    }
  };

export const pollSearch =
  ({ history = null, recursive = true, pollCount = 0, market_type = "B2C" }) =>
  async (dispatch, getState) => {
    const state = getState();
    const maxPollCount = state.HotelSearch.searchMeta.maxPollCount;
    const session_id = state.HotelSearch.searchMeta.session_id;
    if (pollCount >= maxPollCount) {
      dispatch(stopSearch({ history }));
      return;
    }

    const result = await pollAccommodationSearch({ session_id, market_type });
    if (!result && !recursive) {
      toast.error("We couldn't find any hotel results. We are sorry for incovenience", {
        duration: 3000,
      });
      history.push("/search_feedback/hotel");
      return;
    }

    dispatch(setCurrentPollCount(pollCount + 1));

    const results = result?.results ?? [];
    if (results.length > 0) {
      await dispatch(saveHotelResults(results));
    }

    const search_status = result?.status ?? "";
    if (search_status === "working" && recursive) {
      setTimeout(
        () => dispatch(pollSearch({ history, pollCount: pollCount + 1, market_type })),
        1000,
      );
    } else if (state.HotelSearch?.isSearching ?? false) {
      await dispatch(stopSearch({ history }));
    }
  };

function setCurrentPollCount(pollCount) {
  return { type: ACCOMMODATION_SEARCH_SET_CURRENT_POLLCOUNT, pollCount };
}

function saveHotelResults(hotels) {
  return { type: ACCOMMODATION_SEARCH_SAVE_RESULTS, hotels };
}

// ============= HOTELLIST SESSION STORAGE ACTION HELPERS ============
export function getPageSizeFromSession(currentPageSize) {
  const meta = sessionStorage.getItem("hotelListMetadata");

  if (!meta) {
    return currentPageSize;
  }

  const parsed_meta = JSON.parse(meta);
  return parsed_meta?.pageSize ?? currentPageSize;
}
export function setPageSizeToSession(newPageSize) {
  sessionStorage.setItem("hotelListMetadata", JSON.stringify({ pageSize: newPageSize }));
}

export function getHotelIdFromSession() {
  const meta = sessionStorage.getItem("hotelListMetadata");
  if (meta) {
    return JSON.parse(meta)?.hotelId;
  } else {
    return null;
  }
}

export function setHotelIdToSession(hotelId) {
  const meta = sessionStorage.getItem("hotelListMetadata");
  if (!meta) {
    sessionStorage.setItem("hotelListMetadata", JSON.stringify({ hotelId }));
    return;
  }
  const parsed_meta = JSON.parse(meta);
  parsed_meta["hotelId"] = hotelId;
  sessionStorage.setItem("hotelListMetadata", JSON.stringify(parsed_meta));
}

export function clearHotelIdFromSession() {
  const meta = sessionStorage.getItem("hotelListMetadata");
  if (!meta) {
    return;
  }

  const parsed_meta = JSON.parse(meta);
  delete parsed_meta["hotelId"];
  sessionStorage.setItem("hotelListMetadata", JSON.stringify(parsed_meta));
}
// ================ SESSION STORAGE ACTION HELPERS ===============

// ================= LOCAL STORAGE ACTION HELPERS ================

export function searchSessionLoading() {
  return { type: ACCOMMODATION_SEARCH_SESSION_LOADING };
}

export function searchSessionIdle() {
  return { type: ACCOMMODATION_SEARCH_SESSION_IDLE };
}

export const saveAccSearchSession =
  ({ session_data, history }) =>
  async (dispatch) => {
    const { searchData, searchPayload, searchMeta } = session_data;

    const is_valid = checkSessionTimeValidity(searchMeta.timestamp);
    if (!is_valid) {
      history.push("/404/");
      return;
    }

    await dispatch({
      type: ACCOMMODATION_SEARCH_LOAD_STATE,
      data: {
        searchData,
        searchPayload,
        searchMeta,
      },
    });
  };

export const loadAccsFromSession =
  ({ session_data, history }) =>
  async (dispatch) => {
    await dispatch(saveAccSearchSession({ session_data, history }));
    dispatch(
      pollSearch({
        history,
        market_type: session_data?.searchPayload?.market_type || "B2C",
      }),
    );
  };

export function filtersChange(value) {
  return { type: ACCOMMODATION_SEARCH_FILTERS_CHANGE, value };
}

export function resetAccFilter(key) {
  return { type: ACCOMMODATION_SEARCH_RESET_FILTER, key };
}

function checkSessionTimeValidity(timestamp) {
  const dt = DateTime.fromISO(timestamp, { zone: "UTC" });
  const now = DateTime.now();
  return now.diff(dt, "minutes").toObject().minutes <= 30;
}
